import api from '../../api/restaurant/restaurantCatalogExtensionApi';
import {
  CustomResponse,
  RestaurantComboDTO,
  RestaurantComboFilters,
  RestaurantComboPage,
  RestaurantComboRequest,
  RestaurantModifierDTO,
  RestaurantModifierFilters,
  RestaurantModifierGroupFilters,
  RestaurantModifierGroupPage,
  RestaurantModifierPage,
  RestaurantModifierGroupDTO,
  RestaurantModifierGroupRequest,
  RestaurantModifierRequest,
  RestaurantWasteDTO,
  RestaurantWasteFilters,
  RestaurantWastePage,
  RestaurantWasteRequest,
} from '../../types/restaurant/RestaurantCatalogExtensionTypes';
import { appendRestaurantQuery } from './restaurantPageQuery';

const data = <T>(response: any) => response.data as CustomResponse<T>;

const RestaurantCatalogExtensionService = {
  listModifierGroups: async (
    branchId: number,
    filters: RestaurantModifierGroupFilters
  ) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({ page: String(filters.page), size: String(filters.size) }),
      { search: filters.search?.trim(), active: filters.active }
    );
    return (await api.fetchCustom(`/branches/${branchId}/modifier-groups?${query}`))
      .data as RestaurantModifierGroupPage;
  },
  listModifiers: async (
    branchId: number,
    filters: RestaurantModifierFilters
  ) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({ page: String(filters.page), size: String(filters.size) }),
      {
        search: filters.search?.trim(),
        groupId: filters.groupId,
        rawMaterialId: filters.rawMaterialId,
        active: filters.active,
      }
    );
    return (await api.fetchCustom(`/branches/${branchId}/modifiers?${query}`))
      .data as RestaurantModifierPage;
  },
  createModifierGroup: async (branchId: number, body: RestaurantModifierGroupRequest) =>
    data<RestaurantModifierGroupDTO>(
      await api.addCustom(`/branches/${branchId}/modifier-groups`, body),
    ),
  updateModifierGroup: async (branchId: number, id: number, body: RestaurantModifierGroupRequest) =>
    data<RestaurantModifierGroupDTO>(
      await api.modifyCustom(`/branches/${branchId}/modifier-groups/${id}`, body),
    ),
  deleteModifierGroup: async (branchId: number, id: number) =>
    data<null>(
      await api.removeCustom(`/branches/${branchId}/modifier-groups/${id}`),
    ),
  createModifier: async (branchId: number, body: RestaurantModifierRequest) =>
    data<RestaurantModifierDTO>(
      await api.addCustom(`/branches/${branchId}/modifiers`, body),
    ),
  updateModifier: async (branchId: number, id: number, body: RestaurantModifierRequest) =>
    data<RestaurantModifierDTO>(
      await api.modifyCustom(`/branches/${branchId}/modifiers/${id}`, body),
    ),
  deleteModifier: async (branchId: number, id: number) =>
    data<null>(await api.removeCustom(`/branches/${branchId}/modifiers/${id}`)),
  menuItemModifierGroups: async (menuItemId: number) =>
    data<RestaurantModifierGroupDTO[]>(
      await api.fetchCustom(`/menu-items/${menuItemId}/modifier-groups`),
    ),
  assignMenuItemModifierGroups: async (menuItemId: number, modifierGroupIds: number[]) =>
    data<RestaurantModifierGroupDTO[]>(
      await api.modifyCustom(`/menu-items/${menuItemId}/modifier-groups`, { modifierGroupIds }),
    ),
  listCombos: async (branchId: number, filters: RestaurantComboFilters) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({ page: String(filters.page), size: String(filters.size) }),
      {
        search: filters.search?.trim(),
        active: filters.active,
        available: filters.available,
      }
    );
    return (await api.fetchCustom(`/branches/${branchId}/combos?${query}`))
      .data as RestaurantComboPage;
  },
  createCombo: async (branchId: number, body: RestaurantComboRequest) =>
    data<RestaurantComboDTO>(await api.addCustom(`/branches/${branchId}/combos`, body)),
  updateCombo: async (branchId: number, id: number, body: RestaurantComboRequest) =>
    data<RestaurantComboDTO>(await api.modifyCustom(`/branches/${branchId}/combos/${id}`, body)),
  listWaste: async (branchId: number, filters: RestaurantWasteFilters) => {
    const query = appendRestaurantQuery(
      new URLSearchParams({ page: String(filters.page), size: String(filters.size) }),
      {
        search: filters.search?.trim(),
        inventoryId: filters.inventoryId,
        rawMaterialId: filters.rawMaterialId,
        categoryId: filters.categoryId,
        recordedByUserId: filters.recordedByUserId,
        reason: filters.reason,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      }
    );
    return (await api.fetchCustom(`/branches/${branchId}/waste?${query}`))
      .data as RestaurantWastePage;
  },
  createWaste: async (branchId: number, body: RestaurantWasteRequest) =>
    data<RestaurantWasteDTO>(await api.addCustom(`/branches/${branchId}/waste`, body)),
};

export default RestaurantCatalogExtensionService;
